export function Terms() {
  return (
    <article className="prose prose-sm max-w-3xl mx-auto px-6 py-12 prose-headings:font-semibold">
      <h1>Terms of Service</h1>
      <p>
        <em>
          Last updated: 2026-05-09. Operator: Eli Stowe, sole trader, Newcastle
          NSW, Australia.
        </em>
      </p>

      <h2>The service</h2>
      <p>
        Chart to CSV ("the service", "we", "us") extracts tabular data from
        chart screenshots using third-party AI vision models and returns CSV
        and JSON output, with a verification overlay so you can spot misreads
        before trusting the data. Output accuracy is not guaranteed — always
        verify against the source chart. Public accuracy benchmarks are
        published at <a href="/accuracy">/accuracy</a>.
      </p>

      <h2>Acceptable use</h2>
      <ul>
        <li>You may use the service for any lawful purpose.</li>
        <li>
          You must own or have rights to extract data from the chart you
          upload. Don't use the service to circumvent paywalls or licence
          agreements you've agreed to with the chart's source.
        </li>
        <li>
          No automated scraping of the free tier (5 charts / IP / day). For
          batch / API access, contact us about a paid plan.
        </li>
        <li>
          No abusive content. Don't upload imagery that's unlawful, sexually
          explicit, or designed to harass identifiable individuals.
        </li>
        <li>
          Don't try to reverse-engineer, probe, or interfere with the
          service's infrastructure beyond normal use.
        </li>
      </ul>

      <h2>Your account</h2>
      <p>
        Paid users sign in via magic link sent to the email address on file.
        Keep that email account secure. If you suspect your magic link or
        session has been compromised, request a new sign-in to invalidate the
        previous session, or email{" "}
        <a href="mailto:support@chart-to-csv.com">support@chart-to-csv.com</a>.
        You're responsible for activity on your account.
      </p>

      <h2>Billing</h2>
      <ul>
        <li>
          Paid subscription: <strong>$7 per month</strong>, recurring. Charged
          in AUD at Stripe's standard FX rate at time of charge.
        </li>
        <li>
          Auto-renewing. Cancel any time from the customer portal — access
          continues until the end of the current billing period.
        </li>
        <li>
          <strong>14-day refund</strong> on your first charge if the service
          doesn't work for you. Email{" "}
          <a href="mailto:support@chart-to-csv.com">
            support@chart-to-csv.com
          </a>{" "}
          and we'll refund without quibble.
        </li>
        <li>
          Late or failed payments are retried by Stripe per its standard
          smart-retry schedule. Subscriptions paused after three failed
          retries.
        </li>
      </ul>

      <h2>No warranty</h2>
      <p>
        Service provided <strong>as-is</strong>. We do our best to keep
        accuracy high (see <a href="/accuracy">/accuracy</a>) but make no
        guarantees about any individual extraction. Don't rely on the output
        for safety-critical, medical, financial, or regulated decisions
        without human review.
      </p>

      <h2>Service availability</h2>
      <p>
        We don't promise specific uptime. The service depends on third-party
        infrastructure (Cloudflare, Anthropic, Stripe, Resend) — outages on
        their side will affect us. We monitor and respond to issues; no SLA
        is offered.
      </p>

      <h2>Intellectual property</h2>
      <p>
        You retain all rights to the chart images you upload and the CSV /
        JSON we return. We claim no ownership of your inputs or outputs. The
        Chart to CSV name, site, and code are ours.
      </p>

      <h2>Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, our total aggregate liability
        for any claim arising from your use of the service is limited to the
        amount you paid us in the 12 months preceding the claim. We are not
        liable for indirect, consequential, incidental, or special damages,
        including lost profits or data.
      </p>
      <p>
        Nothing in these terms excludes rights you have under the{" "}
        <em>Australian Consumer Law</em> that cannot be lawfully excluded.
      </p>

      <h2>Termination</h2>
      <p>
        You can cancel any time. We can suspend or terminate accounts that
        breach these terms (e.g. abuse, fraud, automated scraping) with email
        notice and a pro-rata refund of any unused subscription period.
      </p>

      <h2>Changes to these terms</h2>
      <p>
        We'll update the "Last updated" date when terms change. For material
        changes (pricing, refund policy, scope of service), paid users will
        be notified by email at least 30 days before the change applies.
        Continued use after a change means you accept the new terms.
      </p>

      <h2>Governing law</h2>
      <p>
        These terms are governed by the laws of New South Wales, Australia.
        Disputes will be resolved in NSW courts unless small-claims tribunal
        is more appropriate. We aim to resolve issues by direct
        correspondence first — email{" "}
        <a href="mailto:support@chart-to-csv.com">support@chart-to-csv.com</a>.
      </p>
    </article>
  );
}
